import {
  CSSProperties,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import DatePicker from 'react-datepicker';
import { ko } from 'date-fns/esm/locale';
import 'react-datepicker/dist/react-datepicker.css';
import styles from 'styles/components/common/display/CustomDatepicker.module.scss';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';
import { ReactComponent as ChevronLeftIcon } from 'assets/icons/chevron_left.svg';
import { ReactComponent as ChevronRightIcon } from 'assets/icons/chevron_right.svg';
import { format } from 'date-fns';
import classNames from 'classnames';

type TProps = {
  date: null | Date;
  setDate?: Dispatch<SetStateAction<Date | null>>;
  calendarStyle?: CSSProperties;
  leftIconFill?: string;
  rightIconFill?: string;
  minDate?: Date | null;
  maxDate?: Date | null;
  placeholder?: string;
  readOnly?: boolean;
  onChange?: (date: Date) => void;
};

const CustomDatePicker = ({
  date,
  setDate,
  calendarStyle,
  leftIconFill,
  rightIconFill,
  minDate,
  maxDate,
  placeholder,
  readOnly,
  onChange,
}: TProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const datePickerRef = useRef<HTMLDivElement | null>(null);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleChange = (date: Date) => {
    setDate && setDate(date);
    onChange && onChange(date);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  return (
    <div onFocus={handleFocus} ref={datePickerRef} className="inline_block">
      <DatePicker
        dateFormat={'yyyy.MM'}
        locale={ko}
        selected={date}
        showPopperArrow={false}
        onChange={handleChange}
        minDate={minDate}
        maxDate={maxDate}
        readOnly={readOnly}
        className={classNames({ [styles.readonly]: readOnly })}
        customInput={
          <div
            className={classNames(styles.custom_input_wrapper, {
              [styles.focused]: isFocused,
            })}
            style={calendarStyle}
          >
            <CalendarIcon
              width={24}
              height={24}
              fill={
                isFocused
                  ? STYLES_VARIABLES.PURPLE_01
                  : STYLES_VARIABLES.GRAY_09
              }
            />
            {date ? (
              <span
                className={classNames(styles.display_date, styles.selected)}
              >
                {format(date, 'yyyy.MM.dd')}
              </span>
            ) : (
              <span className={styles.display_date}>{placeholder}</span>
            )}
          </div>
        }
        renderCustomHeader={({
          date,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <div className={styles.custom_header_wrapper}>
            <button
              type="button"
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
            >
              <ChevronLeftIcon fill={leftIconFill} />
            </button>
            <div className={styles.custom_header_display_date}>
              {date.getFullYear()}년 {parseInt(format(date, 'MM'))}월
            </div>
            <button
              type="button"
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
            >
              <ChevronRightIcon fill={rightIconFill} />
            </button>
          </div>
        )}
      />
    </div>
  );
};

export default CustomDatePicker;
